import { ChampsOrigine } from '../tracabilite/ChampsOrigine';
import { choixAgriculteur, garantirTracabiliteReception } from '../../lib/tracabilite';
import { useEffect, useMemo, useState } from 'react';
import { X, UserPlus } from '../../lib/icons';
import { toast } from 'sonner';
import {
  PARCOURS,
  QUALITES,
  MODES_ACHAT,
  DEVENIRS,
  decompte,
  familleDe,
  createReception,
  fetchTiers,
  avanceDisponible,
  fetchCuves,
  fetchFormats,
  fetchRelevesClients,
  historiqueParcours,
  detecterTiers,
} from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { getTarifs, tarifTriturationPour } from '../../lib/tarifs';
import { figerCours, getCoursDuJour } from '../../lib/cours';
import { useAuth } from '../../lib/auth';
import { ContactQuickForm } from './ContactQuickForm';
import { ContactCombobox } from '../shared/ContactCombobox';
import { sansPerte } from '../../lib/conflitEcriture';

const field =
  'mt-1.5 h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

const initial = () => {
  return {
    date: new Date().toISOString().slice(0, 10),
    parcours: 'client',
    tiersId: '',
    qualite: 'extra_vierge',
    immatriculation: '',
    poidsBrut: '',
    tare: '',
    mode: 'foss',
    foss: '',
    prixFixe: '',
    prixBaseHuile: '',
    calamites: getTarifs().calamitesDefaut === true,
    devenir: 'cuve',
    cuveId: '',
  };
};

export const ReceptionForm = ({ onClose, onCreated }) => {
  const { user } = useAuth();
  const cours = getCoursDuJour();
  const [form, setForm] = useState(initial);  const [ventes, setVentes] = useState({});
  const [tiers, setTiers] = useState([]);
  const [formats, setFormats] = useState([]);const [cuves, setCuves] = useState([]);
  const [releves, setReleves] = useState([]);
  useEffect(() => {
    if (!form.tiersId || !tiers.some(c=>c.id===form.tiersId)) return;
    setForm(p=>p._traceTiers===p.tiersId?p:({...p,...choixAgriculteur(p.tiersId,tiers),_traceTiers:p.tiersId}));
  }, [form.tiersId, tiers]);
  const [nouveauContact, setNouveauContact] = useState(false);
  const [detection, setDetection] = useState(null);
  const [ambigu, setAmbigu] = useState(null);

  // Détection automatique du profil (double rôle) dès qu'un tiers est choisi.
  useEffect(() => {
    if (!form.tiersId) return setDetection(null);
    const c = tiers.find((x) => x.id === form.tiersId);
    if (!c) return setDetection(null);
    setDetection({ contact: c, historique: historiqueParcours(c.id) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.tiersId, tiers]);

  // Saisie d'un nouveau tiers : rapprochement niveau 1/2/3 avant toute action.
  const verifierSaisie = (saisie) => {
    const res = detecterTiers(saisie);
    if (res.type === 'exact' && res.contact) {
      setTiers((l) => (l.some((c) => c.id === res.contact.id) ? l : [...l, res.contact]));
      setForm((f) => ({ ...f, tiersId: res.contact.id }));
      setNouveauContact(false);
      toast.success(
        `Tiers existant reconnu (${res.motif}) : ${res.contact.nom}${
          res.historique?.mixte ? ' — profil mixte Client/Fournisseur' : ''
        }`
      );
      return true;
    }
    if (res.type === 'ambigu' && res.contact) {
      setAmbigu({ saisie, ...res });
      return true;
    }
    return false;
  };

  const contactCree = (contact) => {
    setTiers((l) => (l.some((c) => c.id === contact.id) ? l : [...l, contact].sort((a, b) => a.nom.localeCompare(b.nom))));
    setForm((f) => ({ ...f, tiersId: contact.id }));
    setNouveauContact(false);
  };

  useEffect(() => {
    fetchTiers().then(setTiers);
    fetchFormats().then(setFormats);
    fetchCuves().then(setCuves);
    fetchRelevesClients().then(setReleves);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isClient = form.parcours === 'client';
  const famille = familleDe(form.qualite);
  // Tarif client actif, sinon grille par qualité ; lecture seule à la réception.
  const contactClient = isClient ? tiers.find((c) => c.id === form.tiersId) : null;
  const tarifApplique = tarifTriturationPour(form.qualite, contactClient);
  const lignesVentes = Object.entries(ventes)
    .filter(([, q]) => Number(q) > 0)
    .map(([articleId, qte]) => ({ articleId, qte: Number(qte) }));

  const calc = useMemo(
    () =>
      decompte({
        parcours: form.parcours,
        poidsBrut: form.poidsBrut,
        tare: form.tare,
        mode: isClient ? 'prestation' : form.mode,
        foss: form.foss,
        prixFixe: form.prixFixe,
        prixBaseHuile: form.prixBaseHuile,
        tarifTrituration: tarifApplique,
        calamites: !isClient && form.calamites,
        rendementReel: null,
        ventes: lignesVentes,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, ventes, tarifApplique]
  );

  const cuvesDispo = cuves.filter((c) => c.famille === 'noble');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tiersId) return toast.error('Tiers obligatoire : sélectionnez le client ou le fournisseur du lot');
    if (!Number(form.poidsBrut)) return toast.error('Saisissez le poids brut');
    if (!form.qualite) return toast.error('Sélectionnez la qualité des olives (Extra-Vierge / Vierge / Lampante)');
    const tareSaisie = form.tare !== '' && form.tare !== null;
    if (tareSaisie && Number(form.tare) >= Number(form.poidsBrut)) return toast.error('La tare doit être inférieure au poids brut');
    if (isClient && !tarifApplique)
      return toast.error('Aucun prix de trituration paramétré pour cette filière — Administration › Paramètres.');
    if (!isClient && form.mode === 'foss' && !Number(form.foss)) return toast.error('Saisissez le rendement fosse (%)');
    if (!isClient && form.mode === 'negocier' && !(Number(cours.noble) > 0 && Number(cours.lampante) > 0))
      return toast.error('Saisissez d’abord le Cours de Base du Jour sur la Vue d’ensemble.');
    if (!isClient && (form.mode === 'foss' || form.mode === 'reel') && !Number(form.prixBaseHuile))
      return toast.error('Saisissez le prix de base de l’huile (TND)');
    if (!isClient && form.mode === 'fixe' && !Number(form.prixFixe)) return toast.error('Saisissez le prix négocié');

    const { error: refus, valeur: row } = await sansPerte(() =>
      createReception({
      ...garantirTracabiliteReception(form),
      date: form.date,
      parcours: form.parcours,
      tiersId: form.tiersId,
      qualite: form.qualite,
      immatriculation: (form.immatriculation || '').trim().toUpperCase(),
      poidsBrut: Number(form.poidsBrut),
      tare: tareSaisie ? Number(form.tare) : null,
      mode: isClient ? 'prestation' : form.mode,
      foss: !isClient && form.mode === 'foss' ? Number(form.foss) : null,
      coursFige: !isClient && form.mode === 'negocier' ? figerCours(famille, user?.name) : null,
      prixFixe: !isClient && form.mode === 'fixe' ? Number(form.prixFixe) : null,
      prixBaseHuile:
        !isClient && (form.mode === 'foss' || form.mode === 'reel') ? Number(form.prixBaseHuile) : null,
      tarifTrituration: isClient ? tarifApplique : null,
      calamites: !isClient && form.calamites === true,
      rendementReel: null,
      devenir: isClient ? form.devenir : null,
      cuveId: null,
      ventes: [],
      statut: tareSaisie ? 'En attente' : 'Sur site',
      heureEntree: new Date().toISOString(),
      })
    );
    if (refus) return;
    toast.success(
      tareSaisie
        ? `Réception ${row.id} enregistrée`
        : `Ticket ${row.id} : camion sur site — la tare sera saisie à la sortie`
    );
    onCreated();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" data-testid="reception-form-overlay">
      <form
        onSubmit={submit}
        data-testid="reception-form"
        className="flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l border-slate-200 bg-white"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">Nouvelle réception</h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              Pesée, parcours et mode de calcul
            </p>
          </div>
          <button
            type="button"
            data-testid="reception-form-close"
            onClick={onClose}
            className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          <div><label className={label}>Immatriculation du véhicule</label>
            <input data-testid="reception-immatriculation" maxLength={32} value={form.immatriculation} onChange={set('immatriculation')} placeholder="Ex. 123 TU 4567" className={field} />
          </div>
          <ChampsOrigine value={form} onChange={setForm} />
          <div>
            <label className={label}>Parcours (sélection obligatoire)</label>
            <div className="mt-2 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2">
              {PARCOURS.map((p) => {
                const active = form.parcours === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`parcours-option-${p.id}`}
                    onClick={() => setForm((f) => ({ ...f, parcours: p.id, tiersId: '' }))}
                    className={`px-3 py-3 text-left transition-colors duration-150 ${
                      active ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'
                    }`}
                  >
                    <p className="text-sm font-bold">{p.label}</p>
                    <p className={`text-xs ${active ? 'text-slate-300' : 'text-slate-500'}`}>{p.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Date</label>
              <input data-testid="input-date" type="date" value={form.date} onChange={set('date')} className={field} />
            </div>
            <div>
              <label className={label}>Contact (client / fournisseur — compte courant unique)</label>
              <div className="flex gap-2">
                <ContactCombobox
                  contacts={tiers}
                  value={form.tiersId}
                  onChange={(id) => setForm((p) => ({ ...p, tiersId: id }))}
                  testid="select-tiers"
                  className="-mt-1.5 flex-1"
                />
                <button
                  type="button"
                  data-testid="nouveau-contact-button"
                  onClick={() => setNouveauContact(true)}
                  title="Créer un nouveau contact"
                  className="mt-1.5 flex h-10 flex-shrink-0 items-center gap-1.5 border-2 border-slate-900 px-3 font-heading text-xs font-bold uppercase tracking-[0.06em] text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                >
                  <UserPlus size={15} /> Nouveau
                </button>
              </div>
              {!isClient && form.tiersId && avanceDisponible(form.tiersId) > 0 && (
                <p data-testid="avance-fournisseur-hint" className="mt-1.5 border-l-2 border-amber-500 bg-amber-50 px-2 py-1 font-data text-[11px] text-amber-800">
                  Avance disponible : {fmt(avanceDisponible(form.tiersId), 3)} TND — imputée automatiquement sur ce bon dès que le montant est connu.
                </p>
              )}
              {isClient && form.tiersId && (() => {
                const nom = tiers.find((c) => c.id === form.tiersId)?.nom;
                const fiche = releves.find((r) => r.nom === nom);
                return (
                  <p
                    data-testid="solde-client"
                    className={`mt-1.5 border-l-2 px-2 py-1 font-data text-[11px] ${
                      fiche?.solde > 0
                        ? 'border-sky-600 bg-sky-50 text-sky-800'
                        : 'border-slate-300 bg-slate-50 text-slate-500'
                    }`}
                  >
                    {fiche?.solde > 0
                      ? `Solde huile au compte : ${fmt(fiche.solde, 1)} Kg (déposé ${fmt(fiche.depose, 1)} Kg · retiré ${fmt(fiche.retire, 1)} Kg)`
                      : 'Aucune huile stockée au compte de ce contact.'}
                  </p>
                );
              })()}
              {form.tiersId && detection?.historique && (
                <div
                  data-testid="detection-double-role"
                  className={`mt-2 border-l-2 px-3 py-2 ${
                    detection.historique.mixte
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-slate-300 bg-slate-50'
                  }`}
                >
                  <p className="font-heading text-xs font-bold text-slate-900">
                    {detection.historique.mixte
                      ? 'Profil mixte détecté automatiquement — Client ET Fournisseur'
                      : detection.historique.roles.length
                      ? `Profil connu : ${detection.historique.roles.join(' / ')}`
                      : 'Nouveau tiers — aucun historique de réception'}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {['client', 'fournisseur'].map((r) => {
                      const actif = detection.historique.roles.includes(r);
                      const h = detection.historique[r];
                      return (
                        <span
                          key={r}
                          data-testid={`role-auto-${r}`}
                          className={`flex items-center gap-1.5 border px-2 py-0.5 font-data text-[11px] uppercase tracking-[0.06em] ${
                            actif
                              ? 'border-slate-900 bg-slate-900 text-white'
                              : 'border-slate-300 bg-white text-slate-400'
                          }`}
                        >
                          {actif ? '☑' : '☐'} {r}
                          {h ? ` · ${h.n} lot(s) · ${h.premiere} → ${h.derniere}` : ''}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={label}>Poids brut (Kg)</label>
              <input data-testid="input-poids-brut" type="number" min="0" value={form.poidsBrut} onChange={set('poidsBrut')} className={field} />
            </div>
            <div>
              <label className={label}>Tare (Kg) <span className="normal-case tracking-normal text-slate-400">— vide = à la sortie</span></label>
              <input data-testid="input-tare" type="number" min="0" placeholder="Saisie à la sortie" value={form.tare} onChange={set('tare')} className={field} />
            </div>
            <div>
              <label className={label}>Poids net (Kg)</label>
              <p data-testid="poids-net" className="tabular mt-1.5 flex h-10 items-center border border-slate-200 bg-slate-100 px-3 font-data text-sm font-semibold text-slate-900">
                {fmt(calc.poidsNet)}
              </p>
            </div>
          </div>

          {isClient ? (
            <div data-testid="parcours-client-fields" className="space-y-5">
              <div>
                <label className={label}>Qualité des olives (obligatoire — route le lot vers sa file d’atelier)</label>
                <div className="mt-2 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-3">
                  {QUALITES.map((q) => {
                    const active = form.qualite === q.id;
                    const lamp = q.famille === 'lampante';
                    return (
                      <button
                        key={q.id}
                        type="button"
                        data-testid={`qualite-client-${q.id}`}
                        onClick={() => setForm((f) => ({ ...f, qualite: q.id }))}
                        className={`px-3 py-2.5 text-sm font-semibold transition-colors duration-150 ${
                          active ? (lamp ? 'bg-orange-600 text-white' : 'bg-slate-900 text-white') : 'bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {q.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div data-testid="tarif-trituration-fields">
                <label className={label}>Prix de trituration appliqué (TND / Kg d’olives)</label>
                <input
                  data-testid="input-tarif-trituration"
                  type="number"
                  step="0.001"
                  min="0"
                  value={tarifApplique}
                  readOnly
                  disabled
                  tabIndex={-1}
                  aria-readonly="true"
                  className={`${field} cursor-not-allowed bg-slate-100 text-slate-600`}
                />
                <p className="mt-1.5 font-data text-xs text-slate-500">
                  {fmt(calc.poidsNet)} kg × {fmt(tarifApplique, 3)} TND/kg ={' '}
                  {fmt(calc.trituration, 3)} TND
                  <span data-testid="tarif-trituration-origine" className="ml-1 text-slate-400">
                    {Number.isFinite(contactClient?.tarifTriturationPersonnalise) && tarifApplique === contactClient.tarifTriturationPersonnalise
                      ? '— tarif personnalisé du client' : `— tarif ${famille === 'lampante' ? 'LAMPANTE' : 'NOBLE'} imposé par la grille tarifaire`}
                    (Administration › Paramètres), non modifiable ici
                  </span>
                </p>
              </div>
              <div>
                <label className={label}>Devenir de l'huile</label>
                <div className="mt-2 grid grid-cols-2 gap-px bg-slate-200">
                  {DEVENIRS.map((d) => {
                    const active = form.devenir === d.id;
                    return (
                      <button
                        key={d.id}
                        type="button"
                        data-testid={`devenir-option-${d.id}`}
                        onClick={() => setForm((f) => ({ ...f, devenir: d.id }))}
                        className={`px-3 py-2.5 text-sm font-semibold transition-colors duration-150 ${
                          active ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {form.devenir === 'cuve' && (
                <p data-testid="cuve-fields" className="border-l-2 border-sky-600 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                  La cuve de stockage sera choisie par le Chef d’Atelier au moment de la trituration, selon le volume
                  produit et la place disponible — aucune cuve à préciser au pont-bascule.
                </p>
              )}

              {form.devenir === 'sortie' && (
                <div data-testid="comptoir-fields" className="border-l-2 border-slate-900 bg-slate-50 px-3 py-3">
                  <p className="text-sm font-semibold text-slate-900">Sortie immédiate</p>
                  <p className="mt-1 font-data text-xs text-slate-600">
                    Les emballages fournis au client sont saisis par le Chef d’Atelier lors de la
                    déclaration de sortie d’huile — aucune cuve à choisir ici.
                  </p>
                </div>
              )}

              <p className="font-data text-xs text-slate-500">
                Facturation = poids net × prix de trituration saisi ci-dessus.
              </p>
            </div>
          ) : (
            <div data-testid="parcours-fournisseur-fields" className="space-y-5">
              <div>
                <label className={label}>Qualité des olives (choix strict)</label>
                <div className="mt-2 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-3">
                  {QUALITES.map((q) => {
                    const active = form.qualite === q.id;
                    const lamp = q.famille === 'lampante';
                    return (
                      <button
                        key={q.id}
                        type="button"
                        data-testid={`qualite-option-${q.id}`}
                        onClick={() => setForm((f) => ({ ...f, qualite: q.id }))}
                        className={`px-3 py-2.5 text-sm font-semibold transition-colors duration-150 ${
                          active
                            ? lamp
                              ? 'bg-orange-600 text-white'
                              : 'bg-slate-900 text-white'
                            : 'bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {q.label}
                      </button>
                    );
                  })}
                </div>
                {famille === 'lampante' && (
                  <p data-testid="lampante-warning" className="mt-2 border-l-2 border-orange-500 bg-orange-50 px-3 py-2 text-xs text-orange-800">
                    Filière LAMPANTE : cloisonnement total. Cet achat est exclu des bilans et des
                    cuves d'huiles nobles.
                  </p>
                )}
              </div>

              <div>
                <label className={label}>Mode d'achat et de calcul</label>
                <div className="mt-2 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-4">
                  {MODES_ACHAT.map((m) => {
                    const active = form.mode === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        data-testid={`mode-option-${m.id}`}
                        onClick={() => setForm((f) => ({ ...f, mode: m.id }))}
                        className={`px-3 py-2.5 text-left transition-colors duration-150 ${
                          active ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'
                        }`}
                      >
                        <p className="text-sm font-semibold">{m.label}</p>
                        <p className={`text-xs ${active ? 'text-slate-300' : 'text-slate-500'}`}>{m.hint}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {form.mode === 'negocier' && (
                <div data-testid="mode-negocier-fields" className="space-y-2">
                  <p className="border-l-2 border-amber-600 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Aucun prix à saisir maintenant. Le <b>Cours de Base du Jour</b> sera figé et horodaté sur ce bon
                    (Extra/Vierge {fmt(cours.noble, 3)} · Lampante {fmt(cours.lampante, 3)} TND/kg) et servira de
                    <b> valeur provisoire</b> pour le CMP des cuves. Le prix définitif se négociera ensuite dans
                    « Réception › Achats à négocier ».
                  </p>
                  <p data-testid="negocier-provision" className="font-data text-xs text-slate-500">
                    Provision estimée : {fmt(calc.prixKg, 3)} TND/kg d’olives ({fmt(calc.montant, 3)} TND au cours figé
                    de {fmt(famille === 'lampante' ? cours.lampante : cours.noble, 3)} TND/kg d’huile)
                  </p>
                </div>
              )}

              {form.mode === 'fixe' && (
                <div data-testid="mode-fixe-fields">
                  <label className={label}>Prix fixe négocié (TND / Kg olives)</label>
                  <input data-testid="input-prix-fixe" type="number" step="0.01" min="0" value={form.prixFixe} onChange={set('prixFixe')} className={field} />
                </div>
              )}

              {form.mode === 'foss' && (
                <div data-testid="mode-foss-fields" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={label}>Rendement Fosse (%)</label>
                    <input data-testid="input-foss" type="number" inputMode="decimal" step="0.01" min="0" max="100" value={form.foss} onChange={set('foss')} className={field} />
                  </div>
                  <div>
                    <label className={label}>Prix de base de l’huile (TND)</label>
                    <input data-testid="input-prix-base-huile" type="number" step="0.001" min="0" value={form.prixBaseHuile} onChange={set('prixBaseHuile')} className={field} />
                  </div>
                  <p className="font-data text-xs text-slate-500 sm:col-span-2">
                    Règle de trois : {Number(form.foss || 0).toFixed(2).replace('.', ',')} % × {fmt(form.prixBaseHuile || 0, 3)} TND ={' '}
                    {fmt(calc.prixKg, 3)} TND/kg
                  </p>
                </div>
              )}

              {form.mode === 'reel' && (
                <div data-testid="mode-reel-fields" className="space-y-2">
                  <div>
                    <label className={label}>Prix de base de l’huile convenu (TND)</label>
                    <input data-testid="input-prix-base-huile-reel" type="number" step="0.001" min="0" value={form.prixBaseHuile} onChange={set('prixBaseHuile')} className={field} />
                  </div>
                  <p data-testid="mode-reel-note" className="border-l-2 border-slate-900 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                    Prix mémorisé sur le dossier. Le paiement final sera calculé après le décompteur :
                    poids net × rendement réel × {fmt(form.prixBaseHuile || 0, 3)} TND.
                  </p>
                </div>
              )}

              <label
                data-testid="toggle-calamites"
                className={`flex cursor-pointer items-start gap-3 border px-3 py-3 transition-colors duration-150 ${
                  form.calamites ? 'border-slate-900 bg-slate-50' : 'border-slate-300 bg-white hover:bg-slate-50'
                }`}
              >
                <input
                  data-testid="input-calamites"
                  type="checkbox"
                  checked={form.calamites}
                  onChange={(e) => setForm((f) => ({ ...f, calamites: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-slate-900"
                />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">
                    Déduire le fonds de calamité au fournisseur (1 %)
                  </span>
                  <span className="mt-0.5 block font-data text-xs text-slate-500">
                    Case décochée : net fournisseur inchangé, fonds de 1 % à charge de l’huilerie.
                    La dette envers l’État reste provisionnée.
                  </span>
                  {form.calamites && (
                    <span data-testid="apercu-calamites" className="mt-1.5 block font-data text-xs text-slate-700">
                      Brut {fmt(calc.montantBrut, 3)} TND − retenue {fmt(calc.retenueCalamites, 3)} TND ={' '}
                      <b>net à payer {fmt(calc.montant, 3)} TND</b>
                    </span>
                  )}
                </span>
              </label>
            </div>
          )}

          <div className="border border-slate-200">
            <p className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
              Simulation {isClient ? 'facturation client' : 'décompte fournisseur'}
            </p>
            <dl className="grid grid-cols-2 gap-px bg-slate-200 text-sm">
              {(isClient
                ? [
                    ['Trituration', `${fmt(calc.trituration, 3)} TND`],
                    ['Emballages', `${fmt(calc.emballages, 3)} TND`],
                    ['Total à facturer', `${fmt(calc.montant, 3)} TND`],
                    ['Devenir', form.devenir === 'cuve' ? 'Stockage en cuve' : 'Sortie immédiate'],
                  ]
                : [
                    ['Huile estimée (L)', `${fmt(calc.huile)} L`],
                    ['Prix unitaire', `${fmt(calc.prixKg, 3)} TND/kg`],
                    ['Montant brut', `${fmt(calc.montantBrut, 3)} TND`],
                    ...(form.calamites
                      ? [['Fonds des Calamités (1 %)', `− ${fmt(calc.retenueCalamites, 3)} TND`]]
                      : []),
                    ['Net à payer', `${fmt(calc.montant, 3)} TND`],
                    ['Sens', calc.sens],
                  ]
              ).map(([k, v]) => (
                <div key={k} className="bg-white px-3 py-2">
                  <dt className="text-xs text-slate-500">{k}</dt>
                  <dd className="tabular font-data text-sm font-semibold text-slate-900">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="mt-auto flex gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="submit"
            data-testid="reception-form-submit"
            className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            Enregistrer la réception
          </button>
          <button
            type="button"
            data-testid="reception-form-cancel"
            onClick={onClose}
            className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
          >
            Annuler
          </button>
        </div>
      </form>
      {nouveauContact && (
        <ContactQuickForm
          role={isClient ? 'client' : 'fournisseur'}
          onVerifier={verifierSaisie}
          onClose={() => setNouveauContact(false)}
          onCreated={contactCree}
        />
      )}
      {ambigu && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4" data-testid="alerte-ambiguite">
          <div className="w-full max-w-md border-2 border-amber-500 bg-white">
            <div className="border-b border-slate-200 bg-amber-50 px-5 py-4">
              <p className="font-heading text-base font-black text-slate-900">
                Ce tiers ressemble à « {ambigu.contact.nom} » — confirmer la liaison ?
              </p>
              <p className="mt-1 font-data text-xs text-slate-600">
                {ambigu.motif} · {ambigu.contact.tel || 'sans téléphone'}
                {ambigu.contact.cin ? ` · CIN ${ambigu.contact.cin}` : ''}
                {ambigu.historique?.mixte ? ' · profil mixte Client/Fournisseur' : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 px-5 py-4">
              <button
                type="button"
                data-testid="ambiguite-confirmer"
                onClick={() => {
                  setTiers((l) => (l.some((c) => c.id === ambigu.contact.id) ? l : [...l, ambigu.contact]));
                  setForm((f) => ({ ...f, tiersId: ambigu.contact.id }));
                  setNouveauContact(false);
                  setAmbigu(null);
                  toast.success(`Liaison confirmée avec ${ambigu.contact.nom}`);
                }}
                className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700"
              >
                Oui, c'est le même tiers
              </button>
              <button
                type="button"
                data-testid="ambiguite-nouveau"
                onClick={() => setAmbigu({ ...ambigu, refuse: true }) || setAmbigu(null)}
                className="h-11 border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
              >
                Non, créer un nouveau tiers
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
